import _ from "underscore";
import DriveView from "../DriveView";
import { Device } from "../model/Device";
import { getCurrentVehicle, isNoVehicle } from "../utils/device";
import DVIRLog from "../model/DVIRLog";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import constants from "../constants";
import { getVersion } from "../utils/native/nativeContainer";
import { navigateToAssetSelection } from "../utils/native/nativeAssetSelection";
import { TrailerAttachments } from "../model/TrailerAttachments";
import Messages from "../model/Messages";
import { DatabaseCache } from "../services/databaseCache";
import Api from "../services/api";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import HosProcess from "../services/hosProcess";
import { hashToArray, htmlEscape } from "../utils/vanilla";
import { getAddinsForDashboard } from "../utils/addinLoader";
import { PageLevel } from "../store/mobile/types";
import { MobileActions } from "../store/mobile/actions";
import { getCustomExemptHOSAbbreviation, getTranslatedStatus } from "../utils/hosHelpers";
import Message from "../model/Message";
import DutyStatusLog, { NODEVICEID, NOUSERID } from "../model/DutyStatusLog";
import { handleApi } from "../utils/error";
import pageHTML from "./main.tmpl";
import { isSystemMessage } from "./messaging/helpers";
import { getStartOfDayUserTimezone } from "../utils/date";
import { getServerDate } from "../utils/storage/date";
import { ITrailer } from "../model/Trailer";
import { BaseCache, makeDeviceCache } from "../utils/storage/cache";
import { FeatureFlagName } from "../utils/featureFlagName";
import { MESSAGING_URL } from "../router";
import { ServerFlags } from "../store/data/types";
import type { THosStatus } from "hos-tools";

export default class DashboardView extends DriveView<Backbone.Model> {

        template = _.template(pageHTML);
        messages: Messages;

        protected strings = [
            "{0} unread",
            "No unread",
            "New inspection",
            "No vehicle",
            "{0} trips today",
            "No trips today",
            "HOS",
            "Work Diary",
            "OFF",
            "SB",
            "D",
            "ON",
            "PC",
            "YM",
            "WT"
        ];
        private readonly hosUtils: HosProcess;
        private readonly databaseCache: DatabaseCache;
        private readonly api: Api;
        private readonly trailerAttachmentCollection: TrailerAttachments;
        private readonly trailers: ITrailer[];
        private readonly deviceCache: BaseCache<IDevice | Device>;
        private myGeotabVersion: string;
        private currentVehicle: Device;
        private currentUser: DeepReadonlyObject<IUser> | null;
        private dutyStatusLogs: DutyStatusLog[];
        private dvirLogCollection: DVIRLog[];

        constructor(options: any) {
            super(options, {
                title: "Dashboard",
                activeSection: "home",
                pageLevel: PageLevel.ONE
            });

            this.databaseCache = this.app.databaseCache;
            this.api = this.app.api;
            this.deviceCache = makeDeviceCache(this.app.store);
            this.currentUser = this.app.userService.getCurrentUser();
            this.messages = new Messages(this.app.store, this.currentUser?.id);
            this.messages.fetch();
            this.myGeotabVersion = this.app.store.getState().data.clientVersion;

            this.dvirLogCollection = this.app.dvirLogsService.getDvirLogs();
            this.trailerAttachmentCollection = new TrailerAttachments(this.api, this.app.store);
            this.trailers = this.trailerAttachmentCollection.getCurrentTrailers();
            this.hosUtils = this.app.hosUtils;
            this.dutyStatusLogs = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isDashboardLog()));
            this.storeUnsubscribe = this.app.observeStore(storeState => [storeState.data.clientVersion, storeState.systemSettings], () => {
                this.myGeotabVersion = this.app.store.getState().data.clientVersion;
                this.render();
            });

            // HOS & DVIR sync completion
            this.listenTo(dispatcher, DispatcherEvents.SyncComplete, () => {
                this.dutyStatusLogs = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isDashboardLog()));
                this.dvirLogCollection = this.app.dvirLogsService.getDvirLogs();
                this.render();
            });

            this.listenTo(dispatcher, DispatcherEvents.MessagesChange, () => {
                this.messages.fetch();
                this.render();
            });

            this.listenTo(dispatcher, DispatcherEvents.UserUpdate, () => {
                let selectVehicle = () => {
                    this.app.router.toWorkflow(false);
                };
                void this.updateCurrentVehicle().then(() => {
                    if (!this.currentVehicle) {
                        selectVehicle();
                    }
                    this.render();
                });
                this.currentUser = this.app.userService.getCurrentUser();

                this.render();
            });

            this.listenTo(dispatcher, DispatcherEvents.VehicleSelected, () => {
                this.updateCurrentVehicle()
                    .then(this.render.bind(this))
                    .catch(handleApi);
            });

        }

        /* eslint-disable @typescript-eslint/unbound-method */
        events() {
            return {
                "click #dashboard-dvir:not(.__disabled)": this.navigateDvir,
                "click #dashboard-hos:not(.__disabled)": this.navigateHos,
                "click #dashboard-messaging:not(.__disabled)": this.navigateMessaging,
                "click #dashboard-trips:not(.__disabled)": this.navigateTrips,
                "click #dashboard-inspection": this.navigateInspection,
                "click #dashboard-inspections:not(.__disabled)": this.navigateInspections,
                "click #dashboard-vehicle": this.navigateVehicle,
                "click #dashboard-settings": this.navigateSettings,
                "click .dashboard.addin": this.navigateAddin,
                "click .dashboard.module:not(.__disabled)": this.navigateModule
            } as Backbone.EventsHash;
        }
        /* eslint-enable @typescript-eslint/unbound-method */

        getCurrentHOSStatus () {
            if (!this.currentUser || !this.currentUser.id) {
                // Show as OFF when no logs have been created
                return "";
            }
            let latestLog = this.app.dutyStatusLogService.getLatestLog(this.dutyStatusLogs);
            return latestLog && latestLog.status ? latestLog.status : "";
        }

        render() {
            this.hosUtils.getRuleResult();
            dispatcher.trigger(DispatcherEvents.CompleteWorkflow);
            let trailers = this.trailerAttachmentCollection.getCurrentTrailers();
            let lastVehicleLog;
            let hasNoVehicle = isNoVehicle();
            let canAttemptHos = this.app.securityService.can("attemptHos");
            let canUseHos = this.app.securityService.can("useHos");
            let canSeeHos = this.app.securityService.can("hasHosClearance");
            let canSeeDvir = this.app.securityService.can("viewDvir");
            let canSeeMessaging = this.app.securityService.can("useMessaging");
            let canSeeTrips = this.app.securityService.can("showTrips");
            let canAccessHos = canUseHos || (canAttemptHos && hasNoVehicle);
            let canAccessDvir = canSeeDvir && !hasNoVehicle;
            let canAccessMessaging = canSeeMessaging;
            let canSeeInspectionMode = (canAccessHos || canAccessDvir) && this.app.store.getState().data.ELDJurisdiction !== "EWD";

            // ###############################################################################################
            // ||                                                                                           ||
            // || Conditions & data for Geotab Drive Modules dashboard tiles                                ||
            // ||                                                                                           ||
            // ###############################################################################################
            let isThirdPartyLoggingAllowed = this.app.store.getState().data.serverFlags !== ServerFlags.None;
            let hasCoachingModuleClearance = this.app.securityService.can("viewCoachingSession") || this.app.securityService.can("manageCoachingSession");
            let canAccessCoachingModule = isThirdPartyLoggingAllowed && hasCoachingModuleClearance
                && this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_COACHING_MODULE);
            let canAccessDriverSafetyModule = isThirdPartyLoggingAllowed && this.app.securityService.can("viewDriverSafety");
            let canAccessInspectionsModule = this.app.securityService.can("useAssetInspection");
            let canAccessColdChainModule = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_COLD_CHAIN);
            // canAccessOtherModule
            // canAccessOtherOtherModule
            // ...

            let driverSafetyTileData = {
                id: "driverSafety",
                icon: "#drive-shield-plus",
                title: "Safety",
                enabled: true
            };

            let inspectionsTileData = {
                id: "inspections",
                icon: "#drive-clipboard",
                title: "Inspections",
                enabled: true
            };

            let coldChainTileData = {
                id: "coldChain",
                icon: "#drive-thermometer",
                title: this.getString("Cold Chain"),
                enabled: true
            };

            let coachingTileData = {
                id: "coaching",
                icon: "#drive-target",
                title: "Coaching",
                enabled: true
            };

            // otherModuleTileData
            // otherOtherModuleTileData
            // ...

            let currentHOSStatus = this.getCurrentHOSStatus();
            let renderImpl = () => {
                let dashboardAddins = getAddinsForDashboard(this.databaseCache, this.app.userService);
                let repairNeeded = 0;
                lastVehicleLog = this.app.dvirLogsService.getLatestLog(this.dvirLogCollection, this.currentVehicle.id);
                if (lastVehicleLog && lastVehicleLog.needsRepair()) {
                    repairNeeded++;
                }

                trailers.forEach(trailer => {
                    let lastTrailerLog = this.app.dvirLogsService.getLatestLog(this.dvirLogCollection, trailer.id);

                    if (lastTrailerLog && lastTrailerLog.needsRepair()) {
                        repairNeeded++;
                    }
                });
                this.translator.translate(this.strings).then(() => {
                    let unreadMessageCount = this.getUnreadMessageCount();
                    let unreadMessage = this.getString(canAccessMessaging ? (unreadMessageCount > 0 ? "{0} unread" : "No unread") : "No vehicle");
                    let tripsCount = this.getTripsCount();
                    let tripsMessage = this.getString(tripsCount > 0 ? "{0} trips today" : "No trips today");
                    let isAustralia = this.app.store.getState().data.ELDJurisdiction === "EWD";

                    let blocksCount = ([canSeeHos, canSeeDvir, canSeeMessaging, canSeeTrips].filter(permission => permission).length) % 2 === 0 ? 2 : 3;
                    let customExemptHOSAbbreviation = getCustomExemptHOSAbbreviation(this.app.store.getState().systemSettings);
                    let status = {
                        addinsArray: dashboardAddins,
                        vehicle: this.currentVehicle,
                        trailers: this.trailers && this.trailers.length ? this.trailers.map(trailer => htmlEscape(trailer.name)).join(", ") : null,
                        unreadMessageCount,
                        unreadMessage: unreadMessage.replace("{0}", unreadMessageCount.toString()),
                        tripsCount,
                        tripsMessage: tripsMessage.replace("{0}", tripsCount.toString()),
                        repairNeeded: repairNeeded,
                        hosStatus: currentHOSStatus,
                        translatedHosStatus: getTranslatedStatus(currentHOSStatus as THosStatus, customExemptHOSAbbreviation, this.getString.bind(this)),
                        myGeotabVersion: this.myGeotabVersion.split("-")[0],
                        androidAppVersion: getVersion(),
                        appVersion: constants.APP_VERSION,
                        canSeeHos,
                        canSeeDvir,
                        canSeeMessaging,
                        canSeeInspectionMode,
                        canSeeTrips,
                        canAccessHos,
                        canAccessDvir,
                        canAccessMessaging,
                        blocksCount,
                        customExemptHOSAbbreviation,
                        isAustralia,
                        modulesEnabled: canAccessDriverSafetyModule || canAccessInspectionsModule || canAccessCoachingModule || canAccessColdChainModule, // || canAccessOtherModule ...
                        canAccessDriverSafetyModule,
                        canAccessCoachingModule,
                        canAccessInspectionsModule,
                        canAccessColdChainModule,
                        driverSafetyTileData,
                        coachingTileData,
                        inspectionsTileData,
                        coldChainTileData,
                        hosLabel: this.getString("HOS"),
                        workDiaryLabel: this.getString("Work Diary")
                    };
                    void this.display(this.template(status));
                });

                return this;
            };

            if (!this.currentVehicle) {
                void this.updateCurrentVehicle().then(renderImpl);
            } else {
                renderImpl();
            }
            return this;
        }

        getUnreadMessageCount() {
            let unread = 0;
            let routeIds: {[routeId: string]: boolean} = {};
            let isUnReadMsg = (msg: Message) => {
                let isDirectionToVehicle = msg.isDirectionToVehicle;
                let device = msg.device;
                // Only current device messages that are unread and recipient messages sent from a different user should be shown in the unread message count on the dashboard page
                let recipientMsg = !isDirectionToVehicle && msg.user?.id !== NOUSERID && msg.user?.id !== this.currentUser?.id && device?.id === NODEVICEID;
                let currentDeviceMsg = isDirectionToVehicle && device?.id !== NODEVICEID && device?.id === this.currentVehicle.id;
                return !msg.read && (recipientMsg || currentDeviceMsg);
            };

            this.messages.getMessages(undefined).forEach(msg => {
                    if (isUnReadMsg(msg) && !isSystemMessage(msg)) {
                        let content = msg.messageContent;
                        if (content?.routeId) {
                            if (!routeIds.hasOwnProperty(content.routeId)) {
                                routeIds[content.routeId] = true;
                                unread++;
                            }
                        } else {
                            unread++;
                        }
                    }
                });
            return unread;
        }

        getTripsCount() {
            if (this.app.securityService.can("showTrips")) {
                let currentUser = this.app.userService.getCurrentUser();

                if (currentUser) {
                    let trips = this.app.store.getState().trips;
                    let currentUserTrips = hashToArray(trips && trips[currentUser.id] || {});
                    let startOfDay = getStartOfDayUserTimezone(getServerDate()).toISOString();
                    return currentUserTrips.filter(trip => (trip.start && trip.start >= startOfDay) || (trip.stop && trip.stop >= startOfDay)).length;
                }
            }

            return 0;
        }

        navigateDvir() {
            this.app.router.navigate("dvir/main", true);
        }

        navigateHos() {
            this.app.router.navigate("hos", true);
        }

        navigateMessaging() {
            this.app.router.navigate(MESSAGING_URL, true);
        }

        navigateTrips() {
            this.app.router.navigate("trips/main", true);
        }

        navigateInspection() {
            this.app.router.navigate("inspection/main", true);
        }

        navigateInspections() {
            this.app.router.navigate(`/${ constants.ZENITH_ROUTE }inspections/main`, true);
        }

        navigateVehicle () {
            // DriveNative handles asset selection natively when its routing flag
            // is on; otherwise this falls back to the WebView page (MYG-133100).
            navigateToAssetSelection(() => this.app.router.navigate("assets/main", true));
        }

        navigateSettings () {
            this.app.router.navigate("settings/main", true);
        }

        navigateAddin (e) {
            let id = e.currentTarget.id;
            this.app.router.navigate(`addin,(addinName:${ id })`, true);
        }

        navigateModule (e) {
            let id = e.currentTarget.id;
            this.app.router.navigate(`/${ constants.ZENITH_ROUTE }${ id }`, true);
        }

        remove () {
            this.app.store.dispatch(MobileActions.setPreviousPage("main"));
            super.remove();
            return this;
        }

        private updateCurrentVehicle() {
            let currentVehicle = getCurrentVehicle();
            return new Promise((resolve, reject) => {
                if (currentVehicle) {
                    this.deviceCache.getOrFetch(this.api, { id: currentVehicle.id }).then(devices => {
                        this.currentVehicle = new Device(devices && devices[0] && devices[0][0]);
                        resolve();
                    }).catch(reject);
                } else {
                    reject(new Error("No current vehicle"));
                }
            });
        }
    }
